/**
 * Client-Side Global Console Override for Production
 * 
 * This script globally suppresses console output in production builds
 * while preserving critical error reporting.
 */

const isProduction = process.env.NODE_ENV === 'production' || process.env.REACT_APP_ENV === 'production';

if (isProduction) {
  // Store original console methods
  const originalConsole = {
    log: console.log,
    debug: console.debug,
    info: console.info,
    warn: console.warn,
    error: console.error,
    table: console.table,
    time: console.time,
    timeEnd: console.timeEnd,
    trace: console.trace
  };

  // Error code mapping for production
  const getErrorCode = (message) => {
    const msg = String(message).toLowerCase();
    
    if (msg.includes('payment') || msg.includes('stripe')) return 'CLIENT_PAYMENT_ERROR_001';
    if (msg.includes('auth') || msg.includes('login')) return 'CLIENT_AUTH_ERROR_002';
    if (msg.includes('api') || msg.includes('fetch')) return 'CLIENT_API_ERROR_003';
    if (msg.includes('validation') || msg.includes('form')) return 'CLIENT_VALIDATION_ERROR_004';
    if (msg.includes('network') || msg.includes('connection')) return 'CLIENT_NETWORK_ERROR_005';
    if (msg.includes('component') || msg.includes('render')) return 'CLIENT_COMPONENT_ERROR_006';
    if (msg.includes('route') || msg.includes('navigation')) return 'CLIENT_ROUTE_ERROR_007';
    
    return 'CLIENT_GENERAL_ERROR_999';
  };

  // Override console methods
  console.log = (...args) => {
    const message = args.join(' ');
    
    // Allow React build messages and critical startup info
    if (message.includes('React App') || 
        message.includes('Build') ||
        message.includes('Development server')) {
      originalConsole.log(...args);
    }
    // Otherwise suppress in production
  };

  console.debug = () => {
    // Completely suppress debug logs in production
  };

  console.info = (...args) => {
    // Suppress info logs in production except React build info
    const message = args.join(' ');
    if (message.includes('React App') || message.includes('Build')) {
      originalConsole.info(...args);
    }
  };

  console.warn = (...args) => {
    // Keep React warnings but suppress others
    const message = args.join(' ');
    if (message.includes('Warning: ') && (message.includes('React') || message.includes('DOM'))) {
      originalConsole.warn(...args);
    }
  };

  console.error = (...args) => {
    // Show errors but with generic codes in production
    const message = args.join(' ');
    const errorCode = getErrorCode(message);
    
    // Keep React errors and critical errors
    if (message.includes('React') || 
        message.includes('Uncaught') ||
        message.includes('ChunkLoadError') ||
        message.includes('TypeError')) {
      originalConsole.error('Client Error:', errorCode);
    } else {
      originalConsole.error('Error:', errorCode);
    }
  };

  console.table = () => {
    // Suppress table output in production
  };

  console.time = () => {
    // Suppress timing logs in production
  };

  console.timeEnd = () => {
    // Suppress timing logs in production
  };

  console.trace = (...args) => {
    // Only show traces for critical client errors
    const message = args.join(' ');
    if (message.includes('Uncaught') || message.includes('ChunkLoadError')) {
      originalConsole.error('Client Trace:', getErrorCode(message));
    }
  };

  // Add special methods to force logging when needed
  console.forceLog = originalConsole.log;
  console.forceError = originalConsole.error;
  console.forceWarn = originalConsole.warn;
}

export default { isProduction };